import { Link, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { api } from '../lib/api'
import { saveUser, type User } from '../lib/auth'
import { useI18n } from '../lib/i18n'
import { track } from '../lib/track'

export default function Signup({ onSignup }: { onSignup: (u: User) => void }) {
  const { t } = useI18n()
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const nav = useNavigate()

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setErr(null)
    setBusy(true)
    try {
      const { user } = await api.post<{ user: User }>('/api/auth/signup', {
        fullName,
        email,
        password,
      })
      saveUser(user)
      track('partner_signup')
      onSignup(user)
      nav('/dashboard')
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : t('Signup failed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="section max-w-md py-16">
      <h1 className="text-3xl font-bold text-white mb-2">{t('Become a referral partner')}</h1>
      <p className="text-ink-500 mb-8">
        {t('It is free. No approval needed. You get a unique code like')} <span className="font-mono text-brand-500">DJK-ANA-4821</span> {t('and start earning today.')}
      </p>
      <form onSubmit={submit} className="space-y-4">
        <label className="block">
          <span className="label">{t('Full name')}</span>
          <input required className="input" value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </label>
        <label className="block">
          <span className="label">{t('Email')}</span>
          <input required type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label className="block">
          <span className="label">{t('Password')}</span>
          <input
            required
            type="password"
            minLength={8}
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span className="text-xs text-ink-500 mt-1 block">{t('Minimum 8 characters.')}</span>
        </label>
        {err && <div className="rounded-xl border border-red-500/30 bg-red-500/10 text-red-400 text-sm p-3">{err}</div>}
        <button type="submit" disabled={busy} className="btn-primary w-full">
          {busy ? t('Creating account…') : t('Create free account')}
        </button>
        <p className="text-xs text-ink-500 text-center">
          {t('By joining you agree to the')}{' '}
          <Link to="/terms" className="text-brand-500 hover:underline">{t('Terms')}</Link> {t('and')}{' '}
          <Link to="/privacy" className="text-brand-500 hover:underline">{t('Privacy Policy')}</Link>.
        </p>
      </form>
      <p className="text-sm text-ink-500 mt-6 text-center">
        {t('Already joined?')} <Link to="/login" className="text-brand-500 hover:underline">{t('Sign in')}</Link>
      </p>
    </section>
  )
}
